import Loader from '@/components/Loader';
import CheckboxField from '@/forms/_fields_v2/CheckboxField';
import useEditableField from '@/hooks/useEditableField';
import {useUser} from '@lore/auth';
import {useQueryMutation} from '@lore/query-connect';
import React, {useEffect, useMemo, useState} from 'react';
import { useTranslation } from 'react-i18next';
import EditableString from '@/components/ui/EditableString';
import EditableSelect from '@/components/ui/EditableSelect';


export default function PersonalInformationSection() {
  const { t } = useTranslation();
  const genderOptions = t('user.demographics.gender.selections', { returnObjects: true });
  const ethnicityOptions = t('user.demographics.ethnicity.selections', { returnObjects: true });

  const genderOptionsFormatted = useMemo(() => ({
    data: genderOptions.map((option) => ({ id: option.value, data: { name: option.label } }))
  }), [genderOptions]);

  const ethnicityOptionsFormatted = useMemo(() => ({
    data: ethnicityOptions.map((option) => ({ id: option.value, data: { name: option.label } }))
  }), [ethnicityOptions]);

  const currentUser = useUser();
  const userMutation = useQueryMutation('currentUser.updateSingleton', currentUser);
  const { gender, ethnicity, yearOfBirth, showDemographics } = currentUser.data;

  const genderField = useEditableField(gender, (v) => userMutation.mutateAsync({ gender: v }));
  const ethnicityField = useEditableField(ethnicity, (v) => userMutation.mutateAsync({ ethnicity: v }));
  const yearOfBirthField = useEditableField(yearOfBirth, (v) => userMutation.mutateAsync({ yearOfBirth: v }));

  const [editableShowDemographics, setEditableShowDemographics] = useState(showDemographics);

  useEffect(() => {
    if (userMutation.isLoading) return;

    if (editableShowDemographics !== showDemographics) {
      userMutation.mutateAsync({ showDemographics: editableShowDemographics })
        .catch(() => setEditableShowDemographics(showDemographics));
    }
  }, [editableShowDemographics, showDemographics, userMutation.isLoading, userMutation.mutateAsync]);

  if (!currentUser || currentUser.isLoading) return (<Loader />);

  return (
    <section id="personal-information" className="py-8">
      <h2 className="mb-8 text-2xl font-semibold text-cs-gray-900">Personal information</h2>

      <div className="grid gap-8 md:grid-cols-3">
        <EditableSelect
          label="Gender identity"
          {...genderField}
          options={genderOptionsFormatted}
          editAriaLabel="Edit gender identity"
        />

        <EditableSelect
          label="Ethnicity"
          {...ethnicityField}
          options={ethnicityOptionsFormatted}
          editAriaLabel="Edit ethnicity"
        />

        <EditableString
          label="Year of birth"
          {...yearOfBirthField}
          editAriaLabel="Edit year of birth"
        />

        <CheckboxField
          label="I agree to participate in the demographic survey"
          checked={editableShowDemographics}
          onChange={(event) => setEditableShowDemographics(event.target.checked)}
        />
      </div>
    </section>
  );
}
